import { useEffect, useRef, useState } from 'react';
import { useApp } from '../lib/app-context';

// QR verification of encryption keys: drawing our code (from the crypto library's module grid) and reading
// the other device's with the camera. The code holds binary data, which the browser's own QR reader cannot
// return, so frames are decoded with jsQR (Apache-2.0, loaded only when scanning).

export function QrGrid({ size, modules, label }: { size: number; modules: Uint8ClampedArray; label: string }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const quiet = 4;
  const scale = Math.max(4, Math.floor(260 / (size + quiet * 2)));
  const px = (size + quiet * 2) * scale;
  useEffect(() => {
    const g = ref.current?.getContext('2d');
    if (!g) return;
    g.fillStyle = '#fff';
    g.fillRect(0, 0, px, px);
    g.fillStyle = '#000';
    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) if (modules[y * size + x]) g.fillRect((x + quiet) * scale, (y + quiet) * scale, scale, scale);
    }
  }, [size, modules, scale, px]);
  return <canvas ref={ref} width={px} height={px} className="qr-verify-code" role="img" aria-label={label} />;
}

/** Camera view that reads a QR code and hands over its raw bytes once. */
export function QrVerifyScanner({ onCode, onCancel }: { onCode: (bytes: Uint8ClampedArray) => void; onCancel: () => void }) {
  const { i18n } = useApp();
  const { t } = i18n;
  const video = useRef<HTMLVideoElement>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let done = false;
    const canvas = document.createElement('canvas');
    const g = canvas.getContext('2d', { willReadFrequently: true });
    void (async () => {
      try {
        const [{ default: jsQR }, media] = await Promise.all([
          import('jsqr'),
          navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false }),
        ]);
        stream = media;
        if (done || !video.current) return media.getTracks().forEach((tr) => tr.stop());
        video.current.srcObject = media;
        await video.current.play().catch(() => undefined);
        const tick = () => {
          if (done) return;
          const v = video.current;
          if (v && g && v.videoWidth) {
            // A smaller copy decodes faster and is plenty for a code held up to the camera.
            const scale = Math.min(1, 640 / v.videoWidth);
            canvas.width = Math.round(v.videoWidth * scale);
            canvas.height = Math.round(v.videoHeight * scale);
            g.drawImage(v, 0, 0, canvas.width, canvas.height);
            const img = g.getImageData(0, 0, canvas.width, canvas.height);
            const code = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
            if (code && code.binaryData.length > 0) {
              done = true;
              onCode(Uint8ClampedArray.from(code.binaryData));
              return;
            }
          }
          timer = setTimeout(tick, 120);
        };
        tick();
      } catch {
        setProblem(t('e2ee.qr.cameraProblem'));
      }
    })();
    return () => {
      done = true;
      if (timer) clearTimeout(timer);
      stream?.getTracks().forEach((tr) => tr.stop());
    };
  }, [onCode, t]);

  return (
    <div className="qr-verify-scanner stack">
      {problem ? <p className="alert alert-error">{problem}</p> : <video ref={video} playsInline muted className="qr-verify-video" />}
      <p className="hint">{t('e2ee.qr.scanHint')}</p>
      <div className="row">
        <span className="spacer" />
        <button className="btn" type="button" onClick={onCancel}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
